import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  productIntegrationApi,
  type AddProductsFromIntegrationRequest,
  type AddProductsFromIntegrationResponse,
} from 'services'

// Query keys for consistent caching
export const productIntegrationKeys = {
  all: ['products'] as const,
  lists: () => [...productIntegrationKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...productIntegrationKeys.lists(), filters] as const,
  details: () => [...productIntegrationKeys.all, 'detail'] as const,
  detail: (id: string) => [...productIntegrationKeys.details(), id] as const,
}

type UseAddProductsFromIntegrationOptions = {
  onSuccess?: (data: AddProductsFromIntegrationResponse) => void
  onError?: (error: Error) => void
}

// Hook for bulk product creation (handles single products as well)
export const useAddProductsFromIntegration = (
  options?: UseAddProductsFromIntegrationOptions
) => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    AddProductsFromIntegrationResponse,
    Error,
    AddProductsFromIntegrationRequest
  >({
    mutationFn: productIntegrationApi.addProductsFromIntegration,
    onSuccess: (data) => {
      // Invalidate product lists to refetch with the new products
      queryClient.invalidateQueries({ queryKey: productIntegrationKeys.lists() })

      // Call the success callback
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      // Call the error callback
      options?.onError?.(error)
    },
  })

  return {
    addProductsFromIntegration: mutation.mutate,
    addProductsFromIntegrationAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  }
}
